/**
 * Annuaire des établissements (migration 0705) — helpers PURS.
 *
 * Source : « Annuaire de l'éducation » du ministère de l'Éducation nationale
 * (data.education.gouv.fr, jeu `fr-en-annuaire-education`, Licence Ouverte
 * Etalab 2.0), une ligne par site. On garde les collèges, lycées et EREA
 * ouverts, métropole, DOM et collectivités d'outre-mer. Un UAI à plusieurs
 * sites devient UNE ligne `schools` (site principal) ; les autres sites sont
 * cherchables (`aliases`).
 */
import { REGION_CODES, type RegionCode } from '../live/geo';

/** Une ligne de l'export JSON de l'annuaire (champs utilisés). */
export interface AnnuaireRecord {
  identifiant_de_l_etablissement: string;
  nom_etablissement: string;
  type_etablissement: string | null;
  statut_public_prive: string | null;
  adresse_1: string | null;
  adresse_2: string | null;
  adresse_3: string | null;
  code_postal: string | null;
  code_commune: string | null;
  nom_commune: string | null;
  code_departement: string | null;
  libelle_departement: string | null;
  code_academie: string | null;
  libelle_academie: string | null;
  libelle_region: string | null;
  voie_generale: string | null;
  voie_technologique: string | null;
  voie_professionnelle: string | null;
  post_bac: string | null;
  ministere_tutelle: string | null;
  type_contrat_prive: string | null;
  code_nature: number | null;
  libelle_nature: string | null;
  latitude: number | null;
  longitude: number | null;
  date_maj_ligne: string | null;
  etat: string | null;
}

export type SchoolKind = 'college' | 'lycee' | 'erea' | 'autre';

/** Une ligne de `public.schools` écrite par l'import. */
export interface SchoolRow {
  uai: string;
  name: string;
  kind: SchoolKind;
  sector: 'public' | 'prive' | null;
  private_contract: string | null;
  nature_code: number | null;
  nature_label: string | null;
  address: string | null;
  postal_code: string | null;
  commune_code: string | null;
  city: string | null;
  departement: string | null;
  departement_name: string | null;
  academie_code: string | null;
  academie_name: string | null;
  region: RegionCode | null;
  region_name: string | null;
  latitude: number | null;
  longitude: number | null;
  voie_generale: boolean | null;
  voie_technologique: boolean | null;
  voie_professionnelle: boolean | null;
  post_bac: boolean | null;
  ministry: string | null;
  aliases: string | null;
  is_open: boolean;
  source: 'annuaire-education';
  source_updated_at: string | null;
  imported_at: string;
}

/** Une ligne renvoyée par la RPC `search_schools`. */
export interface SchoolHit {
  id: string;
  uai: string | null;
  name: string;
  kind: SchoolKind;
  sector: 'public' | 'prive' | null;
  nature_label: string | null;
  address: string | null;
  postal_code: string | null;
  city: string | null;
  departement: string | null;
  departement_name: string | null;
  academie_name: string | null;
  region_name: string | null;
  latitude: number | null;
  longitude: number | null;
  distance_km: number | null;
}

const FOLD_FROM = 'àâäáãåāăąçćčďéèêëēėęěíìîïīįñńňóòôöõøōőřśšşťúùûüūůűýÿžźż\'’‘`-.,;:()/«»"_–—&';
const FOLD_TO = 'aaaaaaaaacccdeeeeeeeeiiiiiinnnoooooooorssstuuuuuuuyyzzz                   ';
const FOLD_MAP: ReadonlyMap<string, string> = new Map(
  [...FOLD_FROM].map((c, i) => [c, [...FOLD_TO][i]!]),
);

/**
 * Texte plié pour la recherche — MIROIR de la fonction SQL `public.fold_text`
 * (0705) : minuscules, sans accents ni ponctuation, « st » / « ste » →
 * « saint » / « sainte », espaces simples.
 */
export function foldText(text: string | null | undefined): string {
  const lowered = (text ?? '').toLowerCase().replace(/œ/g, 'oe').replace(/æ/g, 'ae');
  const mapped = [...lowered].map((c) => FOLD_MAP.get(c) ?? c).join('');
  return mapped
    .replace(/(^|[^\p{L}\p{N}_])ste(?=$|[^\p{L}\p{N}_])/gu, '$1sainte')
    .replace(/(^|[^\p{L}\p{N}_])st(?=$|[^\p{L}\p{N}_])/gu, '$1saint')
    .replace(/\s+/g, ' ')
    .trim();
}

/** « 087 » → « 87 », « 02A » → « 2A », « 971 » → « 971 » ; null si inconnu. */
export function departementFromAnnuaire(code: string | null | undefined): string | null {
  if (!code) return null;
  const c = code.trim().toUpperCase();
  const d = c.length === 3 && c.startsWith('0') ? c.slice(1) : c;
  return /^(0[1-9]|[1-8][0-9]|9[0-5]|2[AB]|97[1-8]|98[4-9])$/.test(d) ? d : null;
}

/** Libellé de région de l'annuaire → enum `region_code` ; null hors des 18 régions (collectivités d'outre-mer). */
export function regionFromLabel(label: string | null | undefined): RegionCode | null {
  const key = foldText(label).replace(/ /g, '_');
  return (REGION_CODES as readonly string[]).includes(key) ? (key as RegionCode) : null;
}

const KINDS: Record<string, SchoolKind> = { Collège: 'college', Lycée: 'lycee', EREA: 'erea' };

const flag = (v: string | null | undefined): boolean | null =>
  v === '1' ? true : v === '0' ? false : null;

const clean = (v: string | null | undefined): string | null => {
  const t = (v ?? '').replace(/\s+/g, ' ').trim();
  return t === '' ? null : t;
};

/** Le site principal d'un UAI à plusieurs sites : pas une « annexe » ni un « site de … », puis le nom le plus court. */
function mainSite(sites: AnnuaireRecord[]): AnnuaireRecord {
  const secondary = (r: AnnuaireRecord) => /\bannexe\b|\bsite\b/i.test(r.nom_etablissement);
  return [...sites].sort(
    (a, b) =>
      Number(secondary(a)) - Number(secondary(b)) ||
      a.nom_etablissement.length - b.nom_etablissement.length ||
      (a.code_postal ?? '').localeCompare(b.code_postal ?? ''),
  )[0]!;
}

/**
 * Lignes `schools` d'un export de l'annuaire : collèges, lycées et EREA OUVERTS, une ligne par UAI (les autres
 * sites en `aliases`). PUR.
 */
export function schoolRowsFromAnnuaire(records: AnnuaireRecord[], importedAt: string): SchoolRow[] {
  const byUai = new Map<string, AnnuaireRecord[]>();
  for (const r of records) {
    const uai = clean(r.identifiant_de_l_etablissement)?.toUpperCase();
    if (!uai || !KINDS[r.type_etablissement ?? ''] || (r.etat && r.etat !== 'OUVERT')) continue;
    const list = byUai.get(uai) ?? [];
    list.push(r);
    byUai.set(uai, list);
  }
  const rows: SchoolRow[] = [];
  for (const [uai, sites] of byUai) {
    const r = mainSite(sites);
    const others = sites.filter((s) => s !== r);
    const aliases = [
      ...new Set(
        others.flatMap((s) => [
          clean(s.nom_etablissement),
          clean(s.nom_commune),
          clean(s.code_postal),
        ]),
      ),
    ].filter((v): v is string => !!v && v !== r.nom_etablissement && v !== r.nom_commune);
    const hasGps =
      typeof r.latitude === 'number' &&
      typeof r.longitude === 'number' &&
      Number.isFinite(r.latitude) &&
      Number.isFinite(r.longitude);
    rows.push({
      uai,
      name: clean(r.nom_etablissement) ?? uai,
      kind: KINDS[r.type_etablissement ?? '']!,
      sector:
        r.statut_public_prive === 'Public'
          ? 'public'
          : r.statut_public_prive === 'Privé'
            ? 'prive'
            : null,
      private_contract:
        r.type_contrat_prive && r.type_contrat_prive !== 'SANS OBJET' ? r.type_contrat_prive : null,
      nature_code: r.code_nature ?? null,
      nature_label: clean(r.libelle_nature),
      address:
        [r.adresse_1, r.adresse_2, r.adresse_3].map(clean).filter(Boolean).join(', ') || null,
      postal_code: clean(r.code_postal),
      commune_code: clean(r.code_commune),
      city: clean(r.nom_commune),
      departement: departementFromAnnuaire(r.code_departement),
      departement_name: clean(r.libelle_departement),
      academie_code: clean(r.code_academie),
      academie_name: clean(r.libelle_academie),
      region: regionFromLabel(r.libelle_region),
      region_name: clean(r.libelle_region),
      latitude: hasGps ? r.latitude : null,
      longitude: hasGps ? r.longitude : null,
      voie_generale: flag(r.voie_generale),
      voie_technologique: flag(r.voie_technologique),
      voie_professionnelle: flag(r.voie_professionnelle),
      post_bac: flag(r.post_bac),
      ministry: clean(r.ministere_tutelle),
      aliases: aliases.length ? aliases.join(' · ') : null,
      is_open: true,
      source: 'annuaire-education',
      source_updated_at: clean(r.date_maj_ligne),
      imported_at: importedAt,
    });
  }
  return rows.sort((a, b) => a.uai.localeCompare(b.uai));
}

const KIND_LABELS: Record<SchoolKind, string> = {
  college: 'Collège',
  lycee: 'Lycée',
  erea: 'EREA',
  autre: 'Établissement',
};

/** Ligne secondaire d'un résultat : « Lycée · public · Montpellier (34) ». */
export function schoolSubtitle(
  hit: Pick<SchoolHit, 'kind' | 'sector' | 'city' | 'departement'>,
): string {
  const place = hit.city ? `${hit.city}${hit.departement ? ` (${hit.departement})` : ''}` : null;
  return [
    KIND_LABELS[hit.kind] ?? KIND_LABELS.autre,
    hit.sector === 'prive' ? 'privé' : hit.sector === 'public' ? 'public' : null,
    place,
  ]
    .filter(Boolean)
    .join(' · ');
}
